/**
 * Form（LeapWorldTwin-139）：提交中禁用提交钮、失败保留输入、错误在表单内 Alert、
 * resetKey 整表重建、幂等键经 api-client 请求头传递。
 * 默认只在请求进行中锁住防连点，请求结束就放开；要「成功一次就不许再交」的表单声明 lockAfterSuccess（LeapWorldTwin-1046）。
 * 校验口径 2.0（28-flow-driven §7）：流程驱动场景的校验规则来自流程定义、前后端同用一份，
 * 由 FlowForm 承载；本组件仍只管提交态与反馈，不另写通用校验规则。
 */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type FormHTMLAttributes,
  type ReactNode
} from 'react'
import { Alert } from './Alert'
import { ApiClientError, bindFormIdempotencyKey } from './api-client'
import { FormSubmitContext, OverlayPendingContext } from './form-context'
import { describeFailure } from './error-feedback'
import { Button } from './Button'

export function FailureInfo({ failure }: { failure: ReturnType<typeof describeFailure> }) {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState(false)
  return <div className="mt-1 flex flex-wrap items-center gap-1 text-xs">
    {failure.requestId ? <span>排查编号 {failure.requestId} ·</span> : null}
    <Button size="sm" variant="ghost" onClick={async () => {
      try { await navigator.clipboard.writeText(failure.diagnostics); setCopied(true); setCopyError(false) }
      catch { setCopyError(true) }
    }}>{copied ? '已复制排查信息' : '复制排查信息'}</Button>
    {copyError ? <span role="status">复制失败，请手动记录排查编号。</span> : null}
  </div>
}

type FormSubmitResult = void | boolean | { ok?: boolean; error?: string; status?: number; code?: string }

type FormSubmitHandler = (
  values: Record<string, string>,
  event: FormEvent<HTMLFormElement>
) => FormSubmitResult | Promise<FormSubmitResult>

type FormProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  onSubmit: FormSubmitHandler
  /** `'auto'`：明确拒绝后换号，结果不明时重试复用；显式字符串原样使用。 */
  idempotencyKey?: 'auto' | string
  /** 变化时重建内部状态（pending / 错误 / 幂等键 / 已成功），治弹窗换记录残留。 */
  resetKey?: string | number
  /** 成功后锁住提交，直到 resetKey 变化。默认 false：请求结束即放开，页面吞掉失败也不会锁死。 */
  lockAfterSuccess?: boolean
  /** 失败时是否保留输入。默认 true；受控字段由调用方不要在 catch 里清空。 */
  keepValuesOnError?: boolean
  onPendingChange?: (pending: boolean) => void
  failureContext?: Parameters<typeof describeFailure>[1]
  children: ReactNode
}

function readFormValues(form: HTMLFormElement): Record<string, string> {
  const values: Record<string, string> = {}
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value === 'string') values[key] = value
  }
  return values
}

function failureMessage(result: FormSubmitResult): string | null {
  if (result === false) return '提交失败'
  if (result && typeof result === 'object' && result.ok === false) return result.error?.trim() || '提交失败'
  return null
}

function shouldRotateKey(status: number | undefined, code: string | undefined): boolean {
  return status === 400 || status === 403 || status === 404 || status === 422 ||
    (status === 409 && code === 'IDEMPOTENCY_KEY_REUSED')
}

function FormInner({
  onSubmit,
  idempotencyKey,
  keepValuesOnError = true,
  lockAfterSuccess = false,
  onPendingChange,
  failureContext,
  children,
  className = '',
  ...formProps
}: Omit<FormProps, 'resetKey'>) {
  const [generatedKey, setGeneratedKey] = useState(() => idempotencyKey === 'auto' ? crypto.randomUUID() : undefined)
  const key = idempotencyKey === 'auto' ? generatedKey : idempotencyKey
  const overlay = useContext(OverlayPendingContext)
  const pendingRef = useRef(false)
  const succeededRef = useRef(false)
  const [pending, setPending] = useState(false)
  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<ReturnType<typeof describeFailure> | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    onPendingChange?.(pending)
  }, [pending, onPendingChange])

  useEffect(() => {
    overlay?.setChildPending(pending)
    return () => overlay?.setChildPending(false)
  }, [overlay, pending])

  const context = useMemo(
    () => ({ pending, lockedAfterSuccess: succeeded, error, idempotencyKey: key }),
    [pending, succeeded, error, key]
  )

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      // why: 同步 ref 立刻锁，避免 0ms 定时器留下双击窗口；请求结束即放开，只有声明 lockAfterSuccess 才在成功后锁到 resetKey (LeapWorldTwin-1046)
      if (pendingRef.current || (lockAfterSuccess && succeededRef.current)) return
      pendingRef.current = true
      setPending(true)
      setError(null)
      setFailure(null)
      const unbindIdempotencyKey = bindFormIdempotencyKey(key)
      try {
        const values = readFormValues(event.currentTarget)
        const result = await onSubmit(values, event)
        const fail = failureMessage(result)
        if (fail) {
          setError(fail)
          if (result && typeof result === 'object' && shouldRotateKey(result.status, result.code)) {
            setGeneratedKey(crypto.randomUUID())
          }
          if (!keepValuesOnError) event.currentTarget.reset()
          pendingRef.current = false
          setPending(false)
          return
        }
        if (lockAfterSuccess) {
          succeededRef.current = true
          setSucceeded(true)
        }
        pendingRef.current = false
        setPending(false)
      } catch (err) {
        const message = err instanceof Error ? err.message : '提交失败'
        const described = describeFailure(err, failureContext ?? { action: '提交', object: '表单' })
        setError(failureContext ? described.message : message)
        setFailure(err instanceof ApiClientError ? described : null)
        if (err instanceof ApiClientError && shouldRotateKey(err.status, err.data.code as string | undefined)) {
          setGeneratedKey(crypto.randomUUID())
        }
        if (!keepValuesOnError) formRef.current?.reset()
        pendingRef.current = false
        setPending(false)
      } finally {
        unbindIdempotencyKey()
      }
    },
    [keepValuesOnError, lockAfterSuccess, onSubmit, failureContext, key]
  )

  return (
    <FormSubmitContext.Provider value={context}>
      <form
        {...formProps}
        ref={formRef}
        className={className}
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
        aria-busy={pending || undefined}
      >
        {error ? (
          <Alert data-form-error="" className="mb-3 col-span-full">
            {error}
            {failure ? <FailureInfo failure={failure} /> : null}
          </Alert>
        ) : null}
        {children}
      </form>
    </FormSubmitContext.Provider>
  )
}

export function Form({ resetKey, ...props }: FormProps) {
  return <FormInner key={resetKey ?? 'form'} {...props} />
}
